import { pastePlainText } from '../../helper';
import React, { useEffect, useState, useRef } from 'react';
import Modal from "react-modal";
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from "../../components/select";
import { useTranslation } from "react-i18next";
import { BsFiletypeCsv } from "react-icons/bs";

import useForm from '../../hooks/recommend/useForm';
import validate from "../../validation/recommendByValidationRules";
import { useLanguage } from '../../context/languageContext';
import PreviewImage from '../../components/image/previewImage';
import BulkUploadRecommender from "./bulkUpload";
import Button from '../../components/button';
import RecommendedBooksField from './recommendedBooksField';
import { useAlreadyExists } from "../../components/confirmDialog";



const AddRecommendBy = ({ isAddRecommendedByModalVisible, setIsAddRecommendedByModalVisible, closeModal, onSuccess }) => {
  const { t } = useTranslation();
  const { setSelectedLang, selectedLang, adminInfo, fallbackLang } = useLanguage();
  const showAlreadyExists = useAlreadyExists();

  const [isBulkUploadRecommendedByPopupVisible, setIsBulkUploadRecommendedByPopupVisible] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (fallbackLang) {
      setSelectedLang(fallbackLang);
    }
  }, []);

  const handleAddNewRecommendedData = async () => {
    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/recommend/add`, { values }, { headers });

      if (response.status) {
        onSuccess();
        closeModal();
        toast.success(response?.message);
        setIsSubmit(false);
        setValues({
          image: null,
          mediaLink: [],
          translations: createInitialTranslationsArr(),
        });
      }
    } catch (error) {
      console.error("handleAddNewRecommendedData catch error::", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (!showAlreadyExists(error)) toast.error(errorMessage);
    } finally {
      setIsSubmit(false);
    }
  };

  const {
    values,
    setValues,
    createInitialTranslationsArr,
    errors,
    isSubmit,
    setIsSubmit,
    editMediaError,
    socialMediaLinks,
    recommendeGenres,
    recommendeBooks,
    fieldOptions,
    
    handleGetTranslationByLang,
    handleUpdateTranslationField,
    handleUpdateRecommendedBooks,
    handleUpdateBooksWritten,
    handleAddMediaLinkValues,
    handleUploadMedia,
    handleSubmit
  } = useForm(handleAddNewRecommendedData, validate, adminInfo);

  // Handle local preview when file is selected - single state update, instant preview
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];

    // Clean up previous blob URL if exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    // Generate local preview URL immediately (synchronous, no await)
    if (file && file.type.startsWith('image/')) {
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setPreviewImageUrl(blobUrl); // Single state update
    } else {
      setPreviewImageUrl(null);
    }

    // Call upload handler (async, but doesn't block preview)
    handleUploadMedia(event);
  };

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Reset preview when modal closes
  useEffect(() => {
    if (!isAddRecommendedByModalVisible) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setPreviewImageUrl(null);
    }
  }, [isAddRecommendedByModalVisible]);

  const handleShowBulkUploadRecommendedByPopup = () => {
    setIsBulkUploadRecommendedByPopupVisible(true);
    setIsAddRecommendedByModalVisible(false);
  };

  const handleCloseBulkUploadRecommendedByPopup = () => {
    setIsBulkUploadRecommendedByPopupVisible(false);
  };


  return (
    <React.Fragment>
      <Modal
        isOpen={isAddRecommendedByModalVisible}
        onRequestClose={closeModal}
        className="modal_body_lg"
        overlayClassName="modal_overlay"
      >
        <div className="box_wrapper">
          <div className="d-flex justify-content-between fw-bold mb-3">
            <h3>Add Recommender</h3>
            <button type="button" className="btn btn-outline-dark" onClick={handleShowBulkUploadRecommendedByPopup}>Add Recommenders in Bulk through CSV <BsFiletypeCsv /></button>
          </div>
          <div className="modal_content_lg">
            <div className='container-fluid my-2'>
              <div className='row'>
                <div className='col-sm-12 col-md-7'>
                  <div className="mb-3">
                    <label className="label">{t("recommender.recommenderName")}:</label>
                    <input
                      className="form_control"
                      placeholder={`${t("general.enter")} ${t("recommender.recommenderName")}...`}
                      type="text"
                      name="name"
                      value={handleGetTranslationByLang(selectedLang).name}
                      onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                    />
                    {errors && errors.name && <p className="error-message d-block">{errors.name}</p>}
                  </div>
                  <div className="mb-3">
                    <label className="label">{t("recommender.recommendedGenres")}:</label>
                    <Select
                      className="form-control-md fw-medium p-0"
                      isMulti
                      closeMenuOnSelect={false}
                      name="recommendedGenres"
                      placeholder={`Select ${t(`general.genre`)}...`}
                      value={handleGetTranslationByLang(selectedLang)?.recommendedGenres?.map((genre) => ({ label: genre.genreName, value: genre.genreId })) || []}
                      options={recommendeGenres?.filter((genre) => genre.type === handleGetTranslationByLang(selectedLang)?.type) || []}
                      isDisabled={true}
                    />
                    {errors && errors.recommendedGenres && <p className="error-message d-block">{errors.recommendedGenres}</p>}
                  </div>
                  <div className="mb-3">
                    <label className="label">{t("recommender.professionField")}:</label>
                    <Select
                      className="form-control-md fw-medium p-0"
                      isMulti
                      closeMenuOnSelect={false}
                      name="profession"
                      onChange={(selectedOptions) => {
                        const updatedGenres = selectedOptions.map((option) => ({ professionName: option.label, professionId: option.value }));
                        handleUpdateTranslationField("profession", updatedGenres, selectedLang);
                      }}
                      value={handleGetTranslationByLang(selectedLang)?.profession?.map((option) => ({ label: option.professionName, value: option.professionId })) || []}
                      options={fieldOptions}
                    />
                    {errors && errors.profession && <p className="error-message d-block">{errors.profession}</p>}
                  </div>
                </div>
                <div className="col-sm-12 col-md-5">
                  <label className="label">{t("recommender.socialMediaLink")}</label>
                  <div className="media_link">
                    {socialMediaLinks.map((item, index) => (
                      <div key={index} className="d-flex gap-2 align-items-center my-3 px-3">
                        <img src={item.imgSrc} alt={item.alt} />
                        <input
                          className="form_control"
                          placeholder={`${t("general.enter")} ${t(`recommender.${item.key}`)}...`}
                          type="text"
                          name="mediaLink"
                          onChange={(e) => handleAddMediaLinkValues(e.target.value, item.key)}
                        />
                      </div>
                    ))}
                  </div>
                  {errors && errors.mediaLink && <p className="error-message d-block">{errors.mediaLink}</p>}
                </div>
              </div>
            </div>
            <div className="row my-2 px-3">
              <div className="col-12">
                <RecommendedBooksField
                  items={handleGetTranslationByLang(selectedLang)?.recommendedBooks || []}
                  bookOptions={recommendeBooks}
                  token={adminInfo?.token}
                  onChange={(nextBooks) => handleUpdateRecommendedBooks(nextBooks, selectedLang)}
                  error={errors?.recommendedBooks}
                />
                <RecommendedBooksField
                  label={t("recommender.booksWritten")}
                  hint="Their books and series, in this order. A series shows on their profile with their series."
                  items={handleGetTranslationByLang(selectedLang)?.booksWritten || []}
                  bookOptions={recommendeBooks}
                  token={adminInfo?.token}
                  onChange={handleUpdateBooksWritten}
                />
              </div>
            </div>
            <div className="row my-2 px-3">
            <div className="col-sm-12 col-md-8 mb-3">
              <label className="label">{t("general.about")}:
                {" "}<span className="text-muted small fst-italic mt-1">(Please write about the recommender within given characters — {handleGetTranslationByLang(selectedLang).about?.length || 0}/4500)</span>
              </label>
              <textarea
                className="form_control"
                placeholder={`${t("general.enter")} ${t("general.about")}...`}
                type="text"
                name="about"
                maxLength={4500}
                value={handleGetTranslationByLang(selectedLang).about}
                onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                // Pasted markdown markers ("**The Internet of Money**") are stripped:
                // this field stores plain text, so they would be saved and shown literally.
                onPaste={(event) =>
                  pastePlainText(event, (value) =>
                    handleUpdateTranslationField("about", value, selectedLang)
                  )
                }
                style={{ height: "330px" }}
              />
              {errors && errors.about && <p className="error-message d-block">{errors.about}</p>}
            </div>
            <div className="col-sm-12 col-md-4 mb-3">
                    <PreviewImage
                      label={t("general.coverImage")}
                      uploadMedia={handleFileSelect}
                      image={previewImageUrl}
                      mediaError={editMediaError}
                      error={errors?.image}
                    />
                  </div>
                  </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={isSubmit}>Cancel</button>
            <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Submitting ..." text="Submit" />
          </div>
        </div>
      </Modal>

      <BulkUploadRecommender
        isPopupVisible={isBulkUploadRecommendedByPopupVisible}
        handleClosePopup={handleCloseBulkUploadRecommendedByPopup}
        onSuccess={onSuccess}
      />
    </React.Fragment>
  )
}

export default AddRecommendBy
